'use client'

import { InputSelect, InputText } from '@vinted/web-ui'
import { omit } from 'lodash'
import { Controller, useFormContext } from 'react-hook-form'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { InputBirthdate } from '@marketplace-web/common-components/input-birthdate-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-ui'
import { NationalitySelect } from '@marketplace-web/nationality-select/nationality-select-feature'
import { NationalityCountryModel } from '@marketplace-web/nationality-select/nationality-select-data'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import { Field } from './types'
import useRequiredUboField from './useRequiredUboField'

type Props = {
  isMinimal: boolean
  index: number
}

const UboFields = ({ isMinimal, index }: Props) => {
  const translate = useTranslate('kyc_form.business.ubo')
  const inputBirthdateTranslate = useTranslate()
  const breakpoints = useBreakpoint()
  const {
    register,
    control,
    formState: { errors },
  } = useFormContext()
  const validationMessage = useFormValidationMessage(errors)
  const requiredField = useRequiredUboField()

  function handleCountryChange(onChange: (...event: Array<unknown>) => void) {
    return (country: NationalityCountryModel | null) => {
      onChange(country)
    }
  }

  function renderRequiredFields() {
    return (
      <>
        <InputText
          {...register(`ubos[${index}].${Field.BirthplaceCity}`, {
            required: requiredField(Field.BirthplaceCity),
          })}
          styling={InputText.Styling.Wide}
          title={translate('birthplace_city.title')}
          placeholder={translate('birthplace_city.placeholder')}
          validation={renderValidation(validationMessage(`ubos[${index}].${Field.BirthplaceCity}`))}
        />
        <Controller
          control={control}
          rules={{
            required: requiredField(Field.BirthplaceCountry),
          }}
          name={`ubos[${index}].${Field.BirthplaceCountry}`}
          render={({ field: { onChange } }) => (
            <NationalitySelect
              name={`ubos[${index}].${Field.BirthplaceCountry}`}
              styling={InputSelect.Styling.Wide}
              showSuggestedCountries
              title={translate('birthplace_country.title')}
              placeholder={translate('birthplace_country.placeholder')}
              validation={renderValidation(
                validationMessage(`ubos[${index}].${Field.BirthplaceCountry}`),
              )}
              onChange={handleCountryChange(onChange)}
            />
          )}
        />
        <Controller
          control={control}
          rules={{
            required: requiredField(Field.Nationality),
          }}
          name={`ubos[${index}].${Field.Nationality}`}
          render={({ field: { onChange } }) => (
            <NationalitySelect
              name={`ubos[${index}].${Field.Nationality}`}
              styling={InputSelect.Styling.Wide}
              showSuggestedCountries
              title={translate('nationality.title')}
              placeholder={translate('nationality.placeholder')}
              validation={renderValidation(
                validationMessage(`ubos[${index}].${Field.Nationality}`),
              )}
              onChange={handleCountryChange(onChange)}
            />
          )}
        />
      </>
    )
  }

  function renderExtendedFields() {
    return (
      <>
        <InputText
          {...register(`ubos[${index}].${Field.FirstName}`, {
            required: requiredField(Field.FirstName),
          })}
          styling={InputText.Styling.Wide}
          title={translate('first_name.title')}
          placeholder={translate('first_name.placeholder')}
          validation={renderValidation(validationMessage(`ubos[${index}].${Field.FirstName}`))}
        />
        <InputText
          {...register(`ubos[${index}].${Field.LastName}`, {
            required: requiredField(Field.LastName),
          })}
          styling={InputText.Styling.Wide}
          title={translate('last_name.title')}
          placeholder={translate('last_name.placeholder')}
          validation={renderValidation(validationMessage(`ubos[${index}].${Field.LastName}`))}
        />
        <Controller
          render={({ field }) => {
            const fieldPropsWithoutRef = omit(field, 'ref')

            return (
              <InputBirthdate
                {...fieldPropsWithoutRef}
                isPhone={!!breakpoints.phones}
                styling={InputText.Styling.Wide}
                title={translate('birthday.title')}
                validation={renderValidation(validationMessage(`ubos[${index}].${Field.Birthday}`))}
                translate={inputBirthdateTranslate}
              />
            )
          }}
          name={`ubos[${index}].${Field.Birthday}`}
          control={control}
          defaultValue={null}
          rules={{
            required: requiredField(Field.Birthday),
          }}
        />
      </>
    )
  }

  return (
    <>
      {!isMinimal && renderExtendedFields()}
      {renderRequiredFields()}
    </>
  )
}

export default UboFields
